import React from "react";

import {ConnectedReportingBlock} from "../../atoms/reporting-block";
import {
  LIST_OF_RESIDENTS_REPORT_ID,
  ReportListOfResidents,
  PHOTOS_REPORT_ID,
  ReportPhotos,
  ARBITRATION_AGREEMENT_REPORT_ID,
  ReportArbitrationAgreement,
} from "./reports";
import {
  TIME_TO_CLOSE_BY_FACILITY_CHART_ID,
  AnalyticsTimeToCloseByFacility,
} from "./analytics";
import {
  reportingOverviewSection,
  reportingPhotoPayerSourceId,
} from "./constants";
import {
  ReportingSectionFrame,
  ReportingSectionRenderer,
} from "./ReportingSectionFrame";
import {
  createWorkflowCompletionTimeSection,
  createWorkflowOverviewSection,
  parseWorkflowSharedSectionId,
} from "./reporting-menu/helpers";

const overviewSectionByWorkflowType = new Map<string, ReportingSectionRenderer>();

const OverviewSection = (workflowType: string): ReportingSectionRenderer => {
  const cached = overviewSectionByWorkflowType.get(workflowType);

  if (cached) {
    return cached;
  }

  const renderer: ReportingSectionRenderer = ({
    baseDateRange,
    activeFacilitiesIds,
  }) => (
    <ReportingSectionFrame section={createWorkflowOverviewSection(workflowType)}>
      <ConnectedReportingBlock
        chartId={LIST_OF_RESIDENTS_REPORT_ID}
        hideCardExport
      >
        {reportingBlockSetters => (
          <ReportListOfResidents
            dateRange={baseDateRange}
            facilities={activeFacilitiesIds}
            reportingBlockSetters={reportingBlockSetters}
            workflowType={workflowType}
          />
        )}
      </ConnectedReportingBlock>
    </ReportingSectionFrame>
  );

  overviewSectionByWorkflowType.set(workflowType, renderer);

  return renderer;
};

const completionTimeSectionByWorkflowType = new Map<
  string,
  ReportingSectionRenderer
>();

const CompletionTimeSection = (
  workflowType: string,
): ReportingSectionRenderer => {
  const cached = completionTimeSectionByWorkflowType.get(workflowType);

  if (cached) {
    return cached;
  }

  const renderer: ReportingSectionRenderer = ({
    baseDateRange,
    activeFacilitiesIds,
  }) => (
    <ReportingSectionFrame
      section={createWorkflowCompletionTimeSection(workflowType)}
    >
      <ConnectedReportingBlock
        chartId={TIME_TO_CLOSE_BY_FACILITY_CHART_ID}
        hideCardExport
      >
        {reportingBlockSetters => (
          <AnalyticsTimeToCloseByFacility
            dateRange={baseDateRange}
            facilities={activeFacilitiesIds}
            reportingBlockSetters={reportingBlockSetters}
            workflowType={workflowType}
          />
        )}
      </ConnectedReportingBlock>
    </ReportingSectionFrame>
  );

  completionTimeSectionByWorkflowType.set(workflowType, renderer);

  return renderer;
};

const PhotoPayerSourceSection: ReportingSectionRenderer = ({
  baseDateRange,
  activeFacilitiesIds,
}) => (
  <ReportingSectionFrame section={reportingPhotoPayerSourceId}>
    <ConnectedReportingBlock chartId={PHOTOS_REPORT_ID} hideCardExport>
      {reportingBlockSetters => (
        <ReportPhotos
          dateRange={baseDateRange}
          facilities={activeFacilitiesIds}
          reportingBlockSetters={reportingBlockSetters}
        />
      )}
    </ConnectedReportingBlock>
  </ReportingSectionFrame>
);

const ArbitrationSection: ReportingSectionRenderer = ({
  baseDateRange,
  activeFacilitiesIds,
}) => (
  <ReportingSectionFrame section={reportingOverviewSection}>
    <ConnectedReportingBlock
      chartId={ARBITRATION_AGREEMENT_REPORT_ID}
      hideCardExport
    >
      {reportingBlockSetters => (
        <ReportArbitrationAgreement
          dateRange={baseDateRange}
          facilities={activeFacilitiesIds}
          reportingBlockSetters={reportingBlockSetters}
        />
      )}
    </ConnectedReportingBlock>
  </ReportingSectionFrame>
);

export const generalSectionById: Record<string, ReportingSectionRenderer> = {
  [reportingPhotoPayerSourceId.id]: PhotoPayerSourceSection,
  [reportingOverviewSection.id]: ArbitrationSection,
};

export const getGeneralSectionRenderer = (
  sectionId: string,
): ReportingSectionRenderer | undefined => {
  const sharedSection = parseWorkflowSharedSectionId(sectionId);

  if (sharedSection?.kind === "overview") {
    return OverviewSection(sharedSection.workflowType);
  }

  if (sharedSection?.kind === "completion-time") {
    return CompletionTimeSection(sharedSection.workflowType);
  }

  return generalSectionById[sectionId];
};
